import Image from "next/image";

import cn from "classnames";
import type { CSSProperties } from "react";
import { Image as ImageType } from "sanity";

import { ChevronRightThinIcon } from "@/components/icons/chevronRightThin.icon";
import { generateUrl, Link } from "@/components/link/link.component";
import { RichText } from "@/components/richText/richText.component";
import { isRichTextBrandColorToken } from "@/components/richText/richTextBrandColorTokens";

import {
  IconSize,
  Link as SanityLink,
  RichText as RichTextType,
} from "@/sanity.types";
import { urlFor } from "@/sanity/utils";

const resolveIconSize = (
  override: IconSize | undefined,
  desktop: IconSize | undefined,
  fallback: number,
) => {
  const width = override?.width ?? desktop?.width;
  const height = override?.height ?? desktop?.height;
  const resolvedWidth = width ?? height ?? fallback;
  const resolvedHeight = height ?? width ?? fallback;
  return {
    width: `${resolvedWidth}px`,
    aspect: `${resolvedWidth} / ${resolvedHeight}`,
  };
};

export const HorizontalCardsItem = ({
  icon,
  title,
  description,
  itemLegalDisclaimer,
  ctaLabel,
  ctaLink,
  ctaTextColor,
  iconSize,
  tabletIconSize,
  mobileIconSize,
  className,
  layout = "stacked",
}: HorizontalCardsItemProps) => {
  const imageUrl = icon ? urlFor(icon).url() : null;
  const ctaHref = generateUrl({ link: ctaLink });
  const ctaColorToken = isRichTextBrandColorToken(ctaTextColor)
    ? ctaTextColor
    : undefined;
  const inline = layout === "inline";
  const mobile = resolveIconSize(mobileIconSize, iconSize, 48);
  const tablet = resolveIconSize(tabletIconSize, iconSize, 48);
  const desktop = resolveIconSize(undefined, iconSize, 56);
  const iconSizeStyle = {
    "--icon-width": mobile.width,
    "--md-icon-width": tablet.width,
    "--lg-icon-width": desktop.width,
    "--icon-aspect": mobile.aspect,
    "--md-icon-aspect": tablet.aspect,
    "--lg-icon-aspect": desktop.aspect,
  } as CSSProperties;

  return (
    <div
      data-testid="horizontal-cards-item"
      className={cn(
        "overflow-hidden rounded-[20px]",
        inline
          ? "flex flex-row items-start gap-3 text-left"
          : "flex flex-col items-center gap-[var(--card-image-gap,20px)] text-center md:gap-[var(--md-card-image-gap,20px)] lg:gap-[var(--lg-card-image-gap,20px)]",
        className,
      )}>
      <div
        data-testid="horizontal-cards-item-icon"
        className="text-primary relative flex aspect-(--icon-aspect) w-(--icon-width) max-w-full shrink-0 items-center justify-center md:aspect-(--md-icon-aspect) md:w-(--md-icon-width) lg:aspect-(--lg-icon-aspect) lg:w-(--lg-icon-width)"
        style={iconSizeStyle}>
        {imageUrl ? (
          <Image
            src={imageUrl}
            alt=""
            width={56}
            height={56}
            className="absolute inset-0 size-full object-contain"
            unoptimized
          />
        ) : null}
      </div>
      <div className="flex w-full min-w-0 flex-1 flex-col gap-[var(--card-text-gap,24px)] px-[var(--card-padding,24px)] pb-[var(--card-padding,24px)] md:gap-[var(--md-card-text-gap,24px)] md:px-[var(--md-card-padding,24px)] md:pb-[var(--md-card-padding,24px)] lg:gap-[var(--lg-card-text-gap,24px)] lg:px-[var(--lg-card-padding,32px)] lg:pb-[var(--lg-card-padding,32px)]">
        <RichText>{title}</RichText>

        <RichText>{description}</RichText>

        {itemLegalDisclaimer?.length ? (
          <div data-testid="horizontal-cards-item-legal">
            <RichText>{itemLegalDisclaimer}</RichText>
          </div>
        ) : null}

        {ctaLabel && ctaHref ? (
          <Link
            href={ctaHref}
            target={ctaLink?.openInNewTab ? "_blank" : undefined}
            rel={ctaLink?.openInNewTab ? "noopener noreferrer" : undefined}
            style={
              ctaColorToken
                ? { color: `var(--color-${ctaColorToken})` }
                : undefined
            }
            className={cn(
              "mt-auto inline-flex w-fit items-center gap-1 text-sm underline underline-offset-2",
              !ctaColorToken && "text-greyscale-headline",
            )}>
            {ctaLabel}
            <ChevronRightThinIcon width={16} height={16} aria-hidden />
          </Link>
        ) : null}
      </div>
    </div>
  );
};

export type HorizontalCardsItemProps = {
  icon?: ImageType;
  title?: RichTextType;
  description?: RichTextType;
  itemLegalDisclaimer?: RichTextType;
  ctaLabel?: string;
  ctaLink?: SanityLink;
  ctaTextColor?: string;
  iconSize?: IconSize;
  tabletIconSize?: IconSize;
  mobileIconSize?: IconSize;
  className?: string;
  layout?: "stacked" | "inline";
};
